import classNames from 'classnames';
import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import Pill from '@/components/molecules/pill/pill';
import Link from '@/components/utilities/link/link';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './booking-flow-step-indicator.module.scss';

interface BookingFlowStepIndicatorProps {
  /** The index of the active step */
  activeStepIndex: number;
  /** Additional class names */
  className?: string;
  /** The index of the completed step */
  completedStepIndex: number;
  /** The function that gets triggered on click */
  onClick: (stepIndex: number) => void;
  /** The steps of the booking flow */
  steps: Content.Components['BookingFlowModuleDto']['steps'];
}

const BookingFlowStepIndicator = ({
  activeStepIndex,
  className,
  completedStepIndex,
  onClick,
  steps,
}: BookingFlowStepIndicatorProps) => {
  const isTablet = useWindowSize('tablet');

  const hasDivider = (stepIndex: number): boolean => {
    return stepIndex > 0 && stepIndex < steps.length;
  };

  const hasHalfHighlight = (stepIndex: number): boolean => {
    return stepIndex === activeStepIndex + 1;
  };

  const isActive = (stepIndex: number): boolean => {
    return stepIndex === activeStepIndex;
  };

  const isCompleted = (stepIndex: number): boolean => {
    return stepIndex < completedStepIndex;
  };

  const getStepHeader = (stepIndex: number): string => {
    return steps[stepIndex].summary;
  };

  return (
    <div className={classNames(styles.component, className)}>
      {steps.map((_step, stepIndex) => (
        <React.Fragment key={stepIndex}>
          {hasDivider(stepIndex) && (
            <div
              className={classNames(styles.divider, {
                [styles.isCompleted]:
                  isCompleted(stepIndex) || isActive(stepIndex),
                [styles.halfHighlight]: hasHalfHighlight(stepIndex),
              })}
            />
          )}

          <Link className={styles.content} onClick={() => onClick(stepIndex)}>
            <Pill
              count={stepIndex + 1}
              icon={
                isCompleted(stepIndex) && !isActive(stepIndex)
                  ? 'CheckMark'
                  : undefined
              }
              variant={
                isActive(stepIndex) || isCompleted(stepIndex)
                  ? 'secondary'
                  : 'primary'
              }
            />

            {isTablet && (
              <Typography className={styles.text} tagStyle="bodyMediumBold">
                {getStepHeader(stepIndex)}
              </Typography>
            )}
          </Link>
        </React.Fragment>
      ))}
    </div>
  );
};

BookingFlowStepIndicator.displayName = 'BookingFlowStepIndicator';

export default BookingFlowStepIndicator;
